"use client";

/**
 * JourneyContext — single source of truth for the logged-in user's
 * certification journey data (cert name, phase, progress %).
 *
 * All surfaces that display the current user's journey subscribe here
 * instead of fetching independently. When the user saves changes in
 * their social profile, they call `updateJourney()` to push the new
 * values instantly — no page reload required, just like username/avatar.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useAuth } from "@/context/auth-context";

export interface JourneyData {
  certification: string | null;
  journeyPhase: string | null;
  progressPercentage: number;
  daysInJourney: number;
  journeyStartDate: string | null;
  daysStreak?: number;
}

interface JourneyContextValue {
  journeyData: JourneyData | null;
  /** True while the initial fetch is in flight */
  loading: boolean;
  /** Re-fetch from the API (call after external updates, e.g. admin changes) */
  refreshJourney: () => void;
  /** Optimistically update without a round-trip (call after a successful save) */
  updateJourney: (data: Partial<JourneyData>) => void;
}

const JourneyContext = createContext<JourneyContextValue>({
  journeyData: null,
  loading: false,
  refreshJourney: () => {},
  updateJourney: () => {},
});

export function JourneyProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [journeyData, setJourneyData] = useState<JourneyData | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetchKey, setFetchKey] = useState(0);

  const fetchJourney = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch("/api/user/social-profile");
      const d = await r.json();
      const p = d.socialProfile;
      if (p?.certification) {
        setJourneyData({
          certification: p.certification,
          journeyPhase: p.journeyPhase ?? null,
          progressPercentage: p.progressPercentage ?? 0,
          daysInJourney: p.daysInJourney ?? 0,
          daysStreak: typeof p.daysStreak === "number" && p.daysStreak > 0 ? p.daysStreak : undefined,
          journeyStartDate: p.journeyStartDate ?? null,
        });
      } else {
        setJourneyData(null);
      }
    } catch {
      // non-fatal — stale UI is better than a broken page
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch once when authenticated, and again whenever fetchKey bumps
  useEffect(() => {
    if (!authLoading && isAuthenticated) fetchJourney();
  }, [authLoading, isAuthenticated, fetchKey, fetchJourney]);

  // Re-fetch automatically when the user returns to the tab — handles the
  // case where cert was edited on another page (e.g. social profile → feed)
  useEffect(() => {
    if (!isAuthenticated) return;
    const onFocus = () => fetchJourney();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [isAuthenticated, fetchJourney]);

  const refreshJourney = useCallback(() => setFetchKey((k) => k + 1), []);

  const updateJourney = useCallback((data: Partial<JourneyData>) => {
    setJourneyData((prev) =>
      prev ? { ...prev, ...data } : (data as JourneyData)
    );
  }, []);

  return (
    <JourneyContext.Provider
      value={{ journeyData, loading, refreshJourney, updateJourney }}
    >
      {children}
    </JourneyContext.Provider>
  );
}

export function useJourney() {
  return useContext(JourneyContext);
}
